/**
 * @fileoverview Protected route component.
 * @summary Route guard that requires authentication.
 *
 * @package KaraoNow
 * @module apps/front/src/routes/ProtectedRoute
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { Navigate } from 'react-router-dom'
import { useAppSelector } from '../hooks/redux.js'
import Spinner from '../components/Spinner.js'
import { ADMIN_DASHBOARD_URL } from '@/config/constants.js'

/**
 * @summary Props for ProtectedRoute component.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
interface ProtectedRouteProps {
  children: React.ReactNode
  requiredRole?: 'organizer' | 'singer' | 'superadmin'
}

/**
 * @summary Protected route component that requires authentication.
 * @param {ProtectedRouteProps} props - Component props.
 * @returns {React.ReactElement} Protected route or redirect to login.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, user, isLoading } = useAppSelector((state) => state.auth)

  // Wait for authentication check to complete before redirecting
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="xl" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

   // Redirect superadmins to admin dashboard
   if (user?.role === 'superadmin') {
    return <Navigate to={ADMIN_DASHBOARD_URL} replace />
  }

  return <>{children}</>
}
